import { useMemo } from 'react';

import {
  OperationFields,
  OperationStates,
  OperationTypes,
  FetchOperationsCacheKey
} from '../../../../../operationsTypes';
import { DutyFormData } from '../../DutyForm.types';
import { UserID } from '../../../../../../users/usersTypes';

import { CREATE_OPERATION_QUERY } from '../../../../../queries/createOperation.query';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';

import { useCreateOperation } from '../../../../../hooks/useCreateOperation';

import { dateFnsConvert } from '../../../../../../../utils/dateFnsConvert';

import {
  DUTY_OPERATION_BODY,
  DUTY_OPERATION_SUBJECT
} from '../../../../../operationsConstants';
import { createOperationFormSchema } from './useDutyForm.schema';

interface useDutyFormOptions {
  cacheKeys?: FetchOperationsCacheKey[];
  clientId: UserID;
  onSuccess?: () => void;
}

function useDutyForm({ cacheKeys, onSuccess, clientId }: useDutyFormOptions) {
  const defaultValues = useMemo<DutyFormData>(
    () => ({
      clientId,
      subject: DUTY_OPERATION_SUBJECT,
      body: DUTY_OPERATION_BODY,
      date: null,
      completeDatetime: null,
      state: OperationStates.COMPLETED,
      operationType: OperationTypes.TASK,
      mentionedUserIds: []
    }),
    [clientId]
  );

  const { control, errors, handleSubmitReactHookForm, register, resetForm } =
    useReactHookForm<DutyFormData>({
      defaultValues,
      schema: createOperationFormSchema,
      isModalForm: true
    });

  const {
    createOperation,
    createOperationErrorMessage,
    createOperationReset,
    createOperationLoading
  } = useCreateOperation({
    query: CREATE_OPERATION_QUERY,
    cacheKeys
  });

  return {
    validationErrors: {
      subjectValidationError: errors?.subject?.message,
      dateValidationError: errors?.date?.message,
      completeDatetimeValidationError: errors?.completeDatetime?.message,
      clientIdValidationError: errors?.clientId?.message
    },
    control,
    resetDutyForm: resetForm,
    createOperationReset,
    createOperationErrorMessage,
    createOperationLoading,
    handleLeaveOfAbsence: handleSubmitReactHookForm({
      onSubmit: async (data) =>
        createOperation({
          ...data,
          completeDatetime: data.completeDatetime
            ? dateFnsConvert.toDateTimeWithTimezone(data.completeDatetime)
            : new Date().toISOString(),
          date: data.date
            ? dateFnsConvert.toDateTimeWithTimezone(data.date)
            : new Date().toISOString()
        }).then(() => {
          onSuccess?.();
        }),
      dirtyFieldsOnly: false
    }),

    registerFields: {
      registerDate: register(OperationFields.DATE),
      registerCompleteDatetime: register(OperationFields.COMPLETE_DATETIME),
      registerSubject: register(OperationFields.SUBJECT)
    }
  };
}

export default useDutyForm;
